import JSZip from 'jszip';
import { OutlineNode } from '../types';

type SupportedLang = 'en' | 'zh-CN' | 'zh-HK';

const PASSWORD_PROTECTED_DOCX_ERROR = 'Password-protected Word documents are not supported at this time. Please remove the password and try again.';
const UNSUPPORTED_DOCX_FORMAT_ERROR = 'Unsupported DOCX file format.';

export function getDocxSupportErrorMessage(error: unknown, lang: SupportedLang): string {
  const message = error instanceof Error ? error.message : 'Failed to parse DOCX outline';

  if (message === PASSWORD_PROTECTED_DOCX_ERROR) {
    return lang === 'en'
      ? PASSWORD_PROTECTED_DOCX_ERROR
      : lang === 'zh-HK'
      ? '暫時不支援受密碼保護的 Word 文件。請先移除密碼，再重新嘗試。'
      : '暂不支持受密码保护的 Word 文档。请先移除密码后再试。';
  }

  if (message === UNSUPPORTED_DOCX_FORMAT_ERROR) {
    return lang === 'en'
      ? UNSUPPORTED_DOCX_FORMAT_ERROR
      : lang === 'zh-HK'
      ? '不支援的 DOCX 檔案格式。'
      : '不支持的 DOCX 文件格式。';
  }

  return message;
}

function matchesSignature(bytes: Uint8Array, signature: number[]): boolean {
  if (bytes.length < signature.length) {
    return false;
  }

  return signature.every((value, index) => bytes[index] === value);
}

export async function validateDocxFileSupport(file: File): Promise<ArrayBuffer> {
  const fileContent = await file.arrayBuffer();
  const signature = new Uint8Array(fileContent.slice(0, 8));

  if (matchesSignature(signature, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) {
    throw new Error(PASSWORD_PROTECTED_DOCX_ERROR);
  }

  if (!matchesSignature(signature, [0x50, 0x4b])) {
    throw new Error(UNSUPPORTED_DOCX_FORMAT_ERROR);
  }

  return fileContent;
}

/**
 * Walks over a list of flat heading records and builds a nested outline tree.
 *
 * @param flatItems An array of `{ level, text }` tuples sorted in document order
 * @returns Top-level outline nodes describing the heading hierarchy
 */
function buildNestedOutline(flatItems: { level: number; text: string }[]): OutlineNode[] {
  const root: OutlineNode[] = [];
  const stack: OutlineNode[] = [];

  flatItems.forEach(({ level, text }) => {
    const node: OutlineNode = { level, text, children: [] };

    // Pop the stack until we find the parent of the current node
    while (stack.length > 0 && stack[stack.length - 1].level >= level) {
      stack.pop();
    }

    if (stack.length === 0) {
      // We are at top-level (e.g., Heading 1)
      root.push(node);
    } else {
      // Add as a child of the last item on the stack
      stack[stack.length - 1].children.push(node);
    }

    // Push current node so that its future sub-headings can attach to it
    stack.push(node);
  });

  return root;
}

/**
 * Parses a DOCX file and extracts its heading outline in a **nested JSON** structure.
 *
 * The DOCX file is processed entirely in the browser. Headings are detected by
 * checking paragraph properties for an explicit outline level, or by mapping the
 * paragraph's style to an outline level defined in `word/styles.xml`.
 *
 * @param file The `.docx` file selected by the user
 * @returns A promise that resolves with an array of top-level `OutlineNode`s
 */
export const parseDocxOutline = async (file: File): Promise<OutlineNode[]> => {
  const fileContent = await validateDocxFileSupport(file);
  const zip = await JSZip.loadAsync(fileContent);
  const parser = new DOMParser();

  // --- 1. Build a map from styleId to outline level from styles.xml ---
  const styleToLvlMap: Record<string, number> = {};
  const stylesXmlFile = zip.file('word/styles.xml');
  if (stylesXmlFile) {
    const stylesXmlText = await stylesXmlFile.async('string');
    const stylesDoc = parser.parseFromString(stylesXmlText, 'application/xml');
    const styleNodes = stylesDoc.getElementsByTagName('w:style');

    for (let i = 0; i < styleNodes.length; i++) {
      const s = styleNodes[i];
      const styleId = s.getAttribute("w:styleId");
      const outlineLvlNode = s.querySelector("pPr > outlineLvl");
      const outlineLvlStr = outlineLvlNode?.getAttribute("w:val");

      if (styleId && outlineLvlStr) {
        const outlineLvl = parseInt(outlineLvlStr, 10);
        if (!Number.isNaN(outlineLvl)) {
          // The map stores the 0-based outline level
          styleToLvlMap[styleId] = outlineLvl;
        }
      }
    }
  }

  // --- 2. Parse document.xml and extract headings ---
  const docXmlFile = zip.file('word/document.xml');
  if (!docXmlFile) {
    throw new Error("Could not find 'word/document.xml' in the DOCX file - is this a valid document?");
  }
  const docXmlText = await docXmlFile.async('string');
  const docXml = parser.parseFromString(docXmlText, 'application/xml');

  const flatOutline: { level: number; text: string }[] = [];
  const paragraphs = docXml.getElementsByTagName("w:p");

  for (let i = 0; i < paragraphs.length; i++) {
    const p = paragraphs[i];
    let level: number | undefined;

    // --- Determine heading level in order of precedence ---

    // 1. Highest precedence: Direct outlineLvl on the paragraph itself.
    const directOutlineLvlNode = p.querySelector("pPr > outlineLvl");
    const directOutlineValStr = directOutlineLvlNode?.getAttribute("w:val");
    if (directOutlineValStr) {
      const outlineLvl = parseInt(directOutlineValStr, 10);
      if (!Number.isNaN(outlineLvl)) {
        level = outlineLvl + 1; // Convert 0-based to 1-based
      }
    }

    // 2. Next, check the paragraph's style against our map from styles.xml.
    if (level === undefined) {
      const styleNode = p.querySelector("pPr > pStyle");
      const styleId = styleNode?.getAttribute("w:val");
      if (styleId && styleToLvlMap.hasOwnProperty(styleId)) {
        level = styleToLvlMap[styleId] + 1; // Convert 0-based to 1-based
      }
    }

    // 3. Fallback for documents that use the "HeadingN" convention without explicit outline levels.
    if (level === undefined) {
        const styleNode = p.querySelector("pPr > pStyle");
        const styleVal = styleNode?.getAttribute("w:val")?.toLowerCase();
        if (styleVal?.startsWith("heading")) {
            const levelMatch = styleVal.match(/\d+$/);
            level = levelMatch ? parseInt(levelMatch[0], 10) : 1;
        }
    }

    // --- Extract text and add to outline if it was a heading ---
    if (level !== undefined) {
      // Concatenate all <w:t> nodes to get the full text of the heading.
      let textContent = '';
      const textNodes = p.getElementsByTagName('w:t');
      for (let j = 0; j < textNodes.length; j++) {
        textContent += textNodes[j].textContent;
      }

      if (textContent.trim()) {
        flatOutline.push({ level, text: textContent.trim() });
      }
    }
  }

  return buildNestedOutline(flatOutline);
};
